'use client';

/**
 * @fileoverview Apex contact form (WEB-569).
 *
 * View + Connected split:
 *   - `ContactFormView` — pure RHF form; used by Storybook + RTL
 *   - `ContactForm` — POSTs to same-origin `/api/contact` (Next → backend)
 *
 * No Resend keys in the browser. Honeypot field `website` is visually hidden.
 */
import { useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import { Label } from '@/components/atoms/primitives/label';
import { Textarea } from '@/components/atoms/primitives/textarea';
import { cn } from '@/components/atoms/primitives/utils';

export type ContactFormSubmitStatus = 'idle' | 'loading' | 'success' | 'error' | 'rate_limited';

/**
 * Client form schema (mirrors `@loci/validation` contactInquiryFormSchema).
 * Kept local so RHF + zodResolver get clean input/output types — the shared
 * schema uses a union transform on `subject` that widens poorly for RHF.
 */
const contactFormSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(120, 'Name is too long'),
  email: z.string().trim().toLowerCase().email('Enter a valid email').max(320, 'Email is too long'),
  subject: z.string().trim().max(200, 'Subject is too long').optional(),
  message: z
    .string()
    .trim()
    .min(10, 'Message must be at least 10 characters')
    .max(5000, 'Message is too long'),
  website: z.string().max(500),
});

/** Form values including the honeypot field (always empty for humans). */
export type ContactFormValues = z.infer<typeof contactFormSchema>;

const EMPTY_VALUES: ContactFormValues = {
  name: '',
  email: '',
  subject: undefined,
  message: '',
  website: '',
};

const SUCCESS_MESSAGE = 'Thanks. We received your message and will get back to you soon.';
const RATE_LIMIT_MESSAGE = 'Too many requests. Please try again later.';
const GENERIC_ERROR_MESSAGE = "Couldn't send your message. Please try again.";
/** Bound client wait so a hung `/api/contact` exits loading and stays retryable. */
const CONTACT_TIMEOUT_MS = 10_000;

export interface ContactFormViewProps {
  defaultValues?: Partial<ContactFormValues>;
  onSubmit?: (values: ContactFormValues) => Promise<void> | void;
  submitStatus?: ContactFormSubmitStatus;
  errorMessage?: string | null;
  className?: string;
}

function FieldError({ id, message }: { id: string; message?: string }): ReactElement | null {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="text-sm text-destructive" data-testid={id}>
      {message}
    </p>
  );
}

export function ContactFormView({
  defaultValues,
  onSubmit,
  submitStatus = 'idle',
  errorMessage = null,
  className,
}: ContactFormViewProps): ReactElement {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isSubmitted, touchedFields },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onBlur',
    defaultValues: { ...EMPTY_VALUES, ...defaultValues },
  });

  const isLoading = submitStatus === 'loading';
  const isSuccess = submitStatus === 'success';
  const isRateLimited = submitStatus === 'rate_limited';
  const isError = submitStatus === 'error';
  const formDisabled = isLoading || isSuccess;

  const showNameError = Boolean(errors.name && (touchedFields.name || isSubmitted));
  const showEmailError = Boolean(errors.email && (touchedFields.email || isSubmitted));
  const showMessageError = Boolean(errors.message && (touchedFields.message || isSubmitted));
  const showSubjectError = Boolean(errors.subject && (touchedFields.subject || isSubmitted));

  if (isSuccess) {
    return (
      <div
        className={cn('space-y-3', className)}
        data-testid="contact-form"
        data-status="success"
        data-valid="true"
      >
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-foreground"
          data-testid="contact-form-success"
        >
          {SUCCESS_MESSAGE}
        </p>
      </div>
    );
  }

  return (
    <form
      className={cn('space-y-5', className)}
      data-testid="contact-form"
      data-status={submitStatus}
      data-valid={isValid ? 'true' : 'false'}
      noValidate
      onSubmit={handleSubmit(async (data) => {
        await onSubmit?.(data);
      })}
    >
      {/* Honeypot — hidden from assistive tech and sighted users */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <Label htmlFor="contact-website">Website</Label>
        <Input
          id="contact-website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          data-testid="contact-website"
          {...register('website')}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-name">
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="contact-name"
          type="text"
          autoComplete="name"
          placeholder="Your name"
          aria-required="true"
          aria-invalid={showNameError || undefined}
          aria-describedby={showNameError ? 'contact-name-error' : undefined}
          data-testid="contact-name"
          disabled={formDisabled}
          {...register('name')}
        />
        <FieldError
          id="contact-name-error"
          message={showNameError ? errors.name?.message : undefined}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-email">
          Email <span className="text-destructive">*</span>
        </Label>
        <Input
          id="contact-email"
          type="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-required="true"
          aria-invalid={showEmailError || undefined}
          aria-describedby={showEmailError ? 'contact-email-error' : undefined}
          data-testid="contact-email"
          disabled={formDisabled}
          {...register('email')}
        />
        <FieldError
          id="contact-email-error"
          message={showEmailError ? errors.email?.message : undefined}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-subject">Subject</Label>
        <Input
          id="contact-subject"
          type="text"
          autoComplete="off"
          placeholder="Optional"
          aria-invalid={showSubjectError || undefined}
          aria-describedby={showSubjectError ? 'contact-subject-error' : undefined}
          data-testid="contact-subject"
          disabled={formDisabled}
          {...register('subject')}
        />
        <FieldError
          id="contact-subject-error"
          message={showSubjectError ? errors.subject?.message : undefined}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="contact-message">
          Message <span className="text-destructive">*</span>
        </Label>
        <Textarea
          id="contact-message"
          rows={5}
          placeholder="How can we help?"
          aria-required="true"
          aria-invalid={showMessageError || undefined}
          aria-describedby={showMessageError ? 'contact-message-error' : undefined}
          data-testid="contact-message"
          disabled={formDisabled}
          {...register('message')}
        />
        <FieldError
          id="contact-message-error"
          message={showMessageError ? errors.message?.message : undefined}
        />
      </div>

      {isError || isRateLimited ? (
        <p role="alert" className="text-sm text-destructive" data-testid="contact-form-error">
          {errorMessage ?? (isRateLimited ? RATE_LIMIT_MESSAGE : GENERIC_ERROR_MESSAGE)}
        </p>
      ) : null}

      <Button
        type="submit"
        variant="accent"
        className="w-full sm:w-auto"
        disabled={formDisabled}
        data-testid="contact-submit"
      >
        {isLoading ? 'Sending' : 'Send message'}
      </Button>
    </form>
  );
}

export interface ContactFormProps {
  className?: string;
}

/**
 * Connected apex contact form — posts to `/api/contact` (same-origin proxy).
 */
export function ContactForm({ className }: ContactFormProps): ReactElement {
  const [submitStatus, setSubmitStatus] = useState<ContactFormSubmitStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(values: ContactFormValues): Promise<void> {
    setSubmitStatus('loading');
    setErrorMessage(null);

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), CONTACT_TIMEOUT_MS);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          message: values.message,
          ...(values.subject ? { subject: values.subject } : {}),
          website: values.website ?? '',
        }),
        signal: controller.signal,
      });

      if (response.ok) {
        setSubmitStatus('success');
        return;
      }

      let message: string | null = null;
      try {
        const json: unknown = await response.json();
        if (
          typeof json === 'object' &&
          json !== null &&
          'error' in json &&
          typeof (json as { error: unknown }).error === 'string'
        ) {
          message = (json as { error: string }).error;
        }
      } catch {
        // ignore parse errors — fall through to generic message
      }

      if (response.status === 429) {
        setSubmitStatus('rate_limited');
        setErrorMessage(message ?? RATE_LIMIT_MESSAGE);
        return;
      }

      setSubmitStatus('error');
      setErrorMessage(message ?? GENERIC_ERROR_MESSAGE);
    } catch {
      setSubmitStatus('error');
      setErrorMessage(GENERIC_ERROR_MESSAGE);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  return (
    <ContactFormView
      className={className}
      submitStatus={submitStatus}
      errorMessage={errorMessage}
      onSubmit={handleSubmit}
    />
  );
}
